import { Link } from "react-router-dom";
import { Clock, ArrowRight, Check } from "lucide-react";
import { CATALOG, IMG, fmt } from "@/data/catalog";
import { Stars, AddToCartButton } from "@/components/common/ui";

const P = CATALOG.single;

const ProductCTA = ({ testid }) => (
  <div className="my-12 overflow-hidden rounded-[2.25rem] border border-[#F2E8E6] bg-[#FFFCFA] md:grid md:grid-cols-[260px_1fr]" data-testid={testid}>
    <Link to="/product/leopard-luxe-led-light-therapy-mask" className="block">
      <img src={IMG.product} alt="Leopard Luxe™ LED Light Therapy Mask" className="h-56 w-full object-cover md:h-full" />
    </Link>
    <div className="p-7 md:p-9">
      <span className="text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-[#8A7F86]">Editor's Pick</span>
      <h3 className="mt-2 font-display text-2xl text-[#2C2B2C]">Leopard Luxe™ LED Light Therapy Mask</h3>
      <div className="mt-2 flex items-center gap-2"><Stars n={5} /><span className="text-sm text-[#8A7F86]">4.9 · 1,200+ reviews</span></div>
      <p className="mt-3 text-sm font-light leading-relaxed text-[#6E6168]">
        Four clinical wavelengths across six modes, in a flexible, salon-grade mask designed for ten quiet minutes a day.
      </p>
      <div className="mt-4 flex items-baseline gap-2">
        <span className="font-display text-2xl text-[#2C2B2C]">{fmt(P.price)}</span>
        {P.compareAt > P.price && (
          <>
            <span className="text-sm text-[#8A7F86] line-through">{fmt(P.compareAt)}</span>
            <span className="rounded-full bg-[#FBC4A8] px-2.5 py-0.5 text-[0.6rem] font-bold uppercase tracking-wider text-[#2C2B2C]">Save {fmt(P.compareAt - P.price)}</span>
          </>
        )}
      </div>
      <div className="mt-5 flex flex-col gap-3">
        <AddToCartButton sku="single" label="Add to Bag" variant="primary" testid={`${testid}-add`} className="w-full whitespace-nowrap" />
        <Link to="/product/single" data-testid={`${testid}-shop`}
          className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full border border-[#2C2B2C] px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-[#2C2B2C] transition-all hover:bg-[#2C2B2C] hover:text-[#FFFCFA]">
          See the full page <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  </div>
);

export default function Article() {
  return (
    <article className="bg-white" data-testid="advertorial-article">
      {/* Masthead */}
      <header className="mx-auto max-w-3xl px-6 pt-16 md:pt-24">
        <div className="flex items-center gap-3 text-[0.62rem] font-semibold uppercase tracking-[0.2em] text-[#8A7F86]">
          <span className="rounded-full bg-[#FBC4A8] px-3 py-1 text-[#2C2B2C]">Skin · Wellness</span>
          <span className="rounded-full border border-[#F2E8E6] px-3 py-1">Sponsored</span>
        </div>
        <h1 className="mt-6 font-display text-4xl font-light leading-[1.1] text-[#2C2B2C] sm:text-5xl">
          Why everyone is suddenly obsessed with <span className="text-[#E97C6A]">LED light therapy</span>
        </h1>
        <p className="mt-5 text-lg font-light leading-relaxed text-[#6E6168]">
          The clinic treatment behind that lit-from-within glow has quietly moved into our bathrooms. Here's what the research actually says about light therapy, and how women are recreating it at home.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 border-y border-[#F2E8E6] py-4 text-sm text-[#8A7F86]">
          <span className="font-medium text-[#2C2B2C]">By Eliza Hartley</span>
          <span>·</span><span>Beauty &amp; Wellness</span>
          <span>·</span><span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> 7 min read</span>
        </div>
      </header>

      {/* Hero image */}
      <figure className="mx-auto mt-10 max-w-4xl px-6">
        <div className="overflow-hidden rounded-[2.5rem] border border-[#F2E8E6]">
          <img src={IMG.lifestyleMask} alt="Woman wearing an LED light therapy mask" className="aspect-[16/10] w-full object-cover" />
        </div>
        <figcaption className="mt-3 text-center text-xs font-light italic text-[#8A7F86]">At-home LED masks have become one of beauty's fastest-growing rituals.</figcaption>
      </figure>

      {/* Body */}
      <div className="mx-auto max-w-2xl px-6 py-14 text-[1.06rem] font-light leading-[1.85] text-[#4A4548] [&>h2]:mb-4 [&>h2]:mt-12 [&>h2]:font-display [&>h2]:text-[1.9rem] [&>h2]:font-normal [&>h2]:leading-tight [&>h2]:text-[#2C2B2C] [&>p]:mb-6">
        <p>
          If your feed looks anything like ours, you've seen it: glowing faces lit up in red and blue light, that quiet ten-minute moment that somehow feels like the most luxurious part of the day. LED light therapy has gone from a treatment you'd book at a clinic to something women are doing on the sofa, in a robe, while the kettle boils.
        </p>
        <p>
          But beneath the hype is a technology that skincare professionals have used for decades. So we looked into what light therapy is, what the research suggests it can do, and why so many people are choosing to bring it home.
        </p>

        <h2>So, what exactly is LED light therapy?</h2>
        <p>
          LED (light-emitting diode) therapy, also known as photobiomodulation, uses specific wavelengths of visible and near-infrared light to interact with the skin. Different colours reach different depths: red light tends to work deeper in the dermis, while blue light stays nearer the surface. The idea is beautifully simple, no needles, no heat, no downtime, just light.
        </p>

        <figure className="my-10">
          <div className="overflow-hidden rounded-[2rem] border border-[#F2E8E6]">
            <img src={IMG.deviceDetail} alt="Close-up of LED mask diodes" className="aspect-[16/9] w-full object-cover" />
          </div>
          <figcaption className="mt-3 text-center text-xs font-light italic text-[#8A7F86]">Modern masks pack medical-grade diodes across multiple wavelengths.</figcaption>
        </figure>

        <h2>The benefits people are noticing</h2>
        <p>Researchers studying LED light therapy in general have explored a range of skin benefits across the different wavelengths:</p>
        <ul className="mb-6 space-y-3">
          {[
            "Red & near-infrared light has been studied for supporting collagen and a firmer, smoother look.",
            "Blue light (around 415nm) has shown antibacterial activity against the bacteria linked to breakouts.",
            "Near-infrared light has been explored for deeper repair, circulation and skin-barrier support.",
          ].map((t) => (
            <li key={t} className="flex items-start gap-3">
              <span className="mt-1 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-[#FBC4A8] text-[#2C2B2C]"><Check className="h-3 w-3" strokeWidth={3} /></span>
              <span>{t}</span>
            </li>
          ))}
        </ul>

        <blockquote className="my-10 border-l-2 border-[#FBC4A8] pl-6 font-display text-2xl font-light italic leading-snug text-[#2C2B2C]">
          "It's the rare wellness ritual that asks almost nothing of you, ten minutes, eyes closed, and you're done."
        </blockquote>

        <h2>From the clinic to your bathroom</h2>
        <p>
          For years, the catch was access. A single in-clinic LED facial can cost anywhere from $80 to $150, and results build with regular sessions, which adds up fast. As at-home devices have improved, that maths has flipped: a quality mask you own can pay for itself in a handful of salon visits, then keep going for years.
        </p>
        <p>
          The convenience matters too. The treatments people stick with are the ones that fit real life, and a cordless mask you can wear while you scroll, stretch or wind down is far easier to keep up than another appointment in the diary.
        </p>

        <ProductCTA testid="article-cta-mid" />

        <h2>Meet the mask everyone's talking about</h2>
        <p>
          The Leopard Luxe™ LED Light Therapy Mask was designed on the Gold Coast for exactly this shift, salon-grade light in a form you'll actually want on your shelf. It combines four clinical wavelengths across six modes, from red for that collagen-boosting glow to blue for clarity, in a soft, flexible silicone mask rather than the rigid plastic shells you may have seen.
        </p>
        <p>
          It's cordless and USB-C rechargeable, UV-free and heat-free, and built around a simple ten-minute session. There's a one-touch remote, built-in eye shields, and it arrives gift-ready with a silk carry bag, the kind of object that makes the ritual feel like a treat rather than a task.
        </p>

        <figure className="my-10">
          <div className="overflow-hidden rounded-[2rem] border border-[#F2E8E6]">
            <img src={IMG.apply} alt="Applying the Leopard Luxe LED mask" className="aspect-[16/10] w-full object-cover" />
          </div>
        </figure>

        <h2>A note on expectations</h2>
        <p>
          Skin is personal, and results vary from person to person. The findings above refer to LED light therapy in general rather than to any single device, and Aura Glow makes no medical claims, light therapy is not intended to diagnose, treat or cure any condition. As with any skincare ritual, consistency is what counts, most people use their mask three to five times a week and look for changes over several weeks.
        </p>
        <p className="text-sm italic text-[#8A7F86]">
          Sources on LED light therapy: Wunsch &amp; Matuschka, Photomedicine &amp; Laser Surgery (2014); Gold et al., Journal of Drugs in Dermatology (2011); Avci et al., Seminars in Cutaneous Medicine &amp; Surgery (2013).
        </p>

        <ProductCTA testid="article-cta-end" />
      </div>
    </article>
  );
}
